import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Settings as SettingsIcon,
  User,
  Award,
  Bell,
  Shield,
  Save,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentUser, supabase } from '../../utils/supabase/client';
import {
  getTeacherSettings,
  updateTeacherSettings,
  type TeacherSettings
} from '../../utils/supabase/analytics';

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'grading' | 'preferences' | 'security'>('profile');
  
  // User data
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userBio, setUserBio] = useState('');
  
  // Settings data
  const [settings, setSettings] = useState<TeacherSettings | null>(null);
  const [formData, setFormData] = useState({
    grading_weight_quiz: 70,
    grading_weight_participation: 30,
    allow_late_submission: true,
    late_penalty_percent: 10,
    default_mode: 'live' as 'live' | 'student-paced',
    auto_grade_enabled: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const user = await getCurrentUser();
      if (!user) {
        toast.error('Please sign in');
        return;
      }

      setUserEmail(user.email || '');
      
      // Load user metadata if available
      const { data: userData } = await supabase.auth.getUser();
      if (userData?.user?.user_metadata) {
        setUserName(userData.user.user_metadata.name || '');
        setUserBio(userData.user.user_metadata.bio || '');
      }

      // Load teacher settings
      const teacherSettings = await getTeacherSettings(user.id);
      if (teacherSettings) {
        setSettings(teacherSettings);
        setFormData({
          grading_weight_quiz: teacherSettings.grading_weight_quiz,
          grading_weight_participation: teacherSettings.grading_weight_participation,
          allow_late_submission: teacherSettings.allow_late_submission,
          late_penalty_percent: teacherSettings.late_penalty_percent,
          default_mode: teacherSettings.default_mode,
          auto_grade_enabled: teacherSettings.auto_grade_enabled
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          name: userName,
          bio: userBio
        }
      });

      if (error) throw error;

      toast.success('Profile updated');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGradingSettings = async () => {
    // Validate weights sum to 100
    const totalWeight = formData.grading_weight_quiz + formData.grading_weight_participation;
    if (totalWeight !== 100) {
      toast.error('Grading weights must sum to 100%');
      return;
    }

    setSaving(true);
    try {
      const user = await getCurrentUser();
      if (!user) return;

      await updateTeacherSettings(user.id, {
        grading_weight_quiz: formData.grading_weight_quiz,
        grading_weight_participation: formData.grading_weight_participation,
        allow_late_submission: formData.allow_late_submission,
        late_penalty_percent: formData.late_penalty_percent
      });

      toast.success('Grading settings saved');
      loadData(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving grading settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const user = await getCurrentUser();
      if (!user) return;

      await updateTeacherSettings(user.id, {
        default_mode: formData.default_mode,
        auto_grade_enabled: formData.auto_grade_enabled
      });

      toast.success('Preferences saved');
      loadData();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const updateFormField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card px-4 md:px-6 py-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your account and preferences
          </p>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-border bg-card px-4 md:px-6">
        <div className="flex gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'profile'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('grading')}
            className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'grading'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Award className="w-4 h-4 inline mr-2" />
            Grading
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'preferences'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <SettingsIcon className="w-4 h-4 inline mr-2" />
            Preferences
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'security'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Security
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 md:p-6">
        <div className="max-w-2xl">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      value={userEmail}
                      disabled
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Email cannot be changed
                    </p>
                  </div>

                  <div>
                    <Label>Display Name</Label>
                    <Input
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Your name"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      value={userBio}
                      onChange={(e) => setUserBio(e.target.value)}
                      placeholder="Tell us about yourself..."
                      className="mt-2 min-h-24"
                    />
                  </div>

                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="w-full sm:w-auto"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Profile
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Grading Tab */}
          {activeTab === 'grading' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Grading Settings</h2>
                
                <div className="space-y-6">
                  <div className="bg-muted/30 border border-border rounded-xl p-4">
                    <h3 className="font-semibold mb-3">Grade Weighting</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Configure how different components contribute to the final grade
                    </p>
                    
                    <div className="grid gap-4">
                      <div>
                        <Label>Quiz/Activity Weight (%)</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.grading_weight_quiz}
                            onChange={(e) => updateFormField('grading_weight_quiz', parseInt(e.target.value))}
                            className="flex-1"
                          />
                          <span className="text-sm text-muted-foreground w-12">
                            {formData.grading_weight_quiz}%
                          </span>
                        </div>
                      </div>

                      <div>
                        <Label>Participation Weight (%)</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.grading_weight_participation}
                            onChange={(e) => updateFormField('grading_weight_participation', parseInt(e.target.value))}
                            className="flex-1"
                          />
                          <span className="text-sm text-muted-foreground w-12">
                            {formData.grading_weight_participation}%
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                        <span className="text-sm font-medium">Total Weight:</span>
                        <Badge variant={
                          (formData.grading_weight_quiz + formData.grading_weight_participation) === 100 
                            ? 'default' 
                            : 'destructive'
                        }>
                          {formData.grading_weight_quiz + formData.grading_weight_participation}%
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/30 border border-border rounded-xl p-4">
                    <h3 className="font-semibold mb-3">Late Submissions</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Allow Late Submissions</p>
                          <p className="text-xs text-muted-foreground">
                            Students can submit after deadline
                          </p>
                        </div>
                        <button
                          onClick={() => updateFormField('allow_late_submission', !formData.allow_late_submission)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.allow_late_submission ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.allow_late_submission ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {formData.allow_late_submission && (
                        <div>
                          <Label>Late Penalty (%)</Label>
                          <div className="flex items-center gap-3 mt-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={formData.late_penalty_percent}
                              onChange={(e) => updateFormField('late_penalty_percent', parseInt(e.target.value))}
                              className="flex-1"
                            />
                            <span className="text-sm text-muted-foreground w-16">
                              {formData.late_penalty_percent}% off
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Deduction applied to late submissions
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveGradingSettings}
                    disabled={saving}
                    className="w-full sm:w-auto"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Grading Settings
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Lesson Preferences</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label>Default Lesson Mode</Label>
                    <Select
                      value={formData.default_mode}
                      onValueChange={(v: any) => updateFormField('default_mode', v)}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="live">Live (Teacher-paced)</SelectItem>
                        <SelectItem value="student-paced">Student-Paced</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Default mode for new lessons
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/30 border border-border rounded-xl">
                    <div>
                      <p className="font-medium">Auto-Grading</p>
                      <p className="text-xs text-muted-foreground">
                        Automatically grade multiple choice and true/false questions
                      </p>
                    </div>
                    <button
                      onClick={() => updateFormField('auto_grade_enabled', !formData.auto_grade_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.auto_grade_enabled ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.auto_grade_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <Button
                    onClick={handleSavePreferences}
                    disabled={saving}
                    className="w-full sm:w-auto"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Preferences
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Security Settings</h2>
                
                <div className="space-y-4">
                  <div className="bg-muted/30 border border-border rounded-xl p-4">
                    <h3 className="font-semibold mb-2">Password</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Update your password to keep your account secure
                    </p>
                    <Button variant="outline">
                      Change Password
                    </Button>
                  </div>

                  <div className="bg-muted/30 border border-border rounded-xl p-4">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Two-Factor Authentication
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add an extra layer of security to your account
                    </p>
                    <Button variant="outline">
                      Configure 2FA
                    </Button>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <h3 className="font-semibold mb-2 text-red-800">Danger Zone</h3>
                    <p className="text-sm text-red-700 mb-4">
                      Permanently delete your account and all associated data
                    </p>
                    <Button variant="destructive">
                      Delete Account
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
